import type {ViewMode} from 'bitmovin-player';
import {useCallback, useEffect, useState} from 'react';
import {usePlayerContext} from './usePlayerContext';

/**
 * Handles toggling the playback of the player. There are a variety of edge cases which this
 * function will abstact away from the UI components that trigger a change in playback status.
 * For now, this simply checks if the player is in playing state, and calls the appropriate
 * method on the player.
 *
 * @returns [() => void | a handler for toggling playback, boolean | whether the functionality is available on current device]
 */
export const useFullscreenToggle = (): [() => void, boolean] => {
  const {player} = usePlayerContext();
  const [isAvailable, setIsAvailable] = useState(true);

  useEffect(() => {
    if (!player) return;

    setIsAvailable(player.isViewModeAvailable('fullscreen' as ViewMode));
  }, [player]);

  const onToggle = useCallback(() => {
    if (!player) return;

    const targetViewMode =
      player.getViewMode() === 'fullscreen'
        ? ('inline' as ViewMode)
        : ('fullscreen' as ViewMode);

    player.setViewMode(targetViewMode);
  }, [player]);

  return [onToggle, isAvailable];
};
